import { useMemo, useState } from 'react';
import { t } from 'i18next';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@peacecover/component';
import { ArrowRightSmall } from '@peacecover/icon';
import updateUserLocaleOrCountry from '../components/api/mutations/updateUserLocaleOrCountry';
import useUserQuery from '../components/api/queries/useUserQuery';
import SelectCountryBottomDrawer from '../components/drawer/SelectCountryBottomDrawer';
import SelectLocaleBottomDrawer from '../components/drawer/SelectLocaleBottomDrawer';
import Countries from '../constants/Countries';
import SupportedLocales from '../constants/SupportedLocales';
import i18n from '../i18n/i18n';
import saveData from '../utils/saveData';

interface LocaleCountrySettingState {
  nextPath?: string;
}

function getLocaleObject(locale?: string) {
  if (locale === 'zh-HK') {
    return {
      code: 'zh-TW',
      name: '繁體中文'
    };
  }

  return SupportedLocales.find((item) => item.code === locale) || SupportedLocales[0];
}

export default function LocaleCountrySettingPage() {
  const user = useUserQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const state = (location.state as LocaleCountrySettingState | null) ?? null;

  const [isShowCountryBottomSheet, setIsShowCountryBottomSheet] = useState(false);
  const [isShowLocaleBottomSheet, setIsShowLocaleBottomSheet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nextPath = typeof state?.nextPath === 'string' ? state.nextPath : '/home';

  const countryObject = useMemo(() => {
    return Countries.find((country) => country.code === user?.country);
  }, [user?.country]);

  const localeObject = useMemo(() => getLocaleObject(user?.locale), [user?.locale]);

  const onConfirmedCountryCode = async (countryCode: string) => {
    if (!user?.userId) return;

    await updateUserLocaleOrCountry({
      userId: user.userId,
      locale: undefined,
      country: countryCode
    });
    queryClient.invalidateQueries(['users']);
  };

  const onConfirmedLocale = async (locale: string) => {
    if (!user?.userId) return;

    await updateUserLocaleOrCountry({
      userId: user.userId,
      locale,
      country: undefined
    });
    saveData('i18nextLng', locale);
    i18n.changeLanguage(locale);
    queryClient.invalidateQueries(['users']);
  };

  const moveToNextPath = () => {
    if (nextPath === '/home') {
      navigate('/home', {
        replace: true,
        state: {
          redirectUrl: '/home'
        }
      });
      return;
    }

    navigate(nextPath, { replace: true });
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    moveToNextPath();
  };

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark items-center w-[100vw] max-w-[570px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-[570px] px-[24px] pt-[24px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px]">
          {t('socialLocaleCountrySettingPage.title')}
        </h1>
        <p className="text-[15px] mt-[14px] text-text-main-lv4 whitespace-pre-line leading-[18px]">
          {t('socialLocaleCountrySettingPage.description')}
        </p>
      </div>

      <div className="w-full max-w-[570px] px-[24px] mt-[28px] flex flex-col gap-[28px]">
        <div className="flex flex-col gap-[10px]">
          <span className="text-[15px] leading-[18px] font-semibold text-text-main-lv2">
            {t('socialLocaleCountrySettingPage.countryTitle')}
          </span>
          <button
            type="button"
            onClick={() => setIsShowCountryBottomSheet(true)}
            className="w-full rounded-[12px] px-[17px] py-[15px] border border-separator2 bg-pure-white dark:bg-pure-black"
          >
            <div className="flex items-center justify-between">
              <span className="text-[17px] font-semibold text-text-main-lv2">
                {countryObject?.name || '-'}
              </span>
              <ArrowRightSmall className="text-text-main-lv1" />
            </div>
          </button>
        </div>

        <div className="flex flex-col gap-[10px]">
          <span className="text-[15px] leading-[18px] font-semibold text-text-main-lv2">
            {t('socialLocaleCountrySettingPage.localeTitle')}
          </span>
          <button
            type="button"
            onClick={() => setIsShowLocaleBottomSheet(true)}
            className="w-full rounded-[12px] px-[17px] py-[15px] border border-separator2 bg-pure-white dark:bg-pure-black"
          >
            <div className="flex items-center justify-between">
              <span className="text-[17px] font-semibold text-text-main-lv2">
                {localeObject?.name || '-'}
              </span>
              <ArrowRightSmall className="text-text-main-lv1" />
            </div>
          </button>
        </div>
      </div>

      <div className="mt-auto w-full max-w-[570px] px-[24px] pb-[max(20px,env(safe-area-inset-bottom))]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {t('next')}
        </Button>
      </div>

      {user && isShowCountryBottomSheet && (
        <SelectCountryBottomDrawer
          isOpen={isShowCountryBottomSheet}
          onClose={setIsShowCountryBottomSheet}
          focusRef={undefined}
          onConfirmedCountryCode={onConfirmedCountryCode}
          defaultCountryCode={user.country}
        />
      )}

      {user && isShowLocaleBottomSheet && (
        <SelectLocaleBottomDrawer
          isOpen={isShowLocaleBottomSheet}
          onClose={setIsShowLocaleBottomSheet}
          focusRef={undefined}
          onConfirmedLocale={onConfirmedLocale}
          defaultLocale={localeObject?.code}
        />
      )}
    </div>
  );
}
